/**
 * spaceLootSystem.ts
 * Pool + drop + collision pipeline for W2 enemy-kill loot pickups
 * (Star Seeds).
 *
 * Lifecycle:
 *  - `maybeSpawnSpaceLoot(x, y)` — called from `spaceEnemySystem` on
 *    each enemy kill. Rolls {@link SPACE_STAR_SEED_DROP_CHANCE}; on a
 *    hit it acquires from the pool and pushes into `spaceState.loot`
 *    with a stable id (`${spawnId}` keeps the renderer key stable
 *    across the pickup's whole life).
 *  - `updateSpaceLoot(dt)` — per-tick drift + reap.
 *  - `resolveShipLootCollisions()` — ship-vs-pickup AABB; on contact
 *    the id appends to a module-local per-session list exposed via
 *    {@link getCollectedSeedIdsThisSession}.
 *
 * Mirrors {@link ./spaceShardSystem} module-for-module. Pickups are
 * silent — no audio, no damage, no iframes. Audio + sparkle land
 * in a follow-up.
 *
 * @module spaceLootSystem
 */

import { ObjectPool } from '../utils/objectPool';
import { SpaceStarSeed } from '../entities/SpaceStarSeed';
import { spaceState } from './spaceState';
import {
  SPACE_STAR_SEED_POOL_CAP,
  SPACE_STAR_SEED_DROP_CHANCE,
} from '../../constants/spaceLootConstants';
import type { AABB } from '../types/entity';
import type { LevelRuntime } from './gameState';

const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

const seedPool = new ObjectPool<SpaceStarSeed>(
  () => new SpaceStarSeed(),
  SPACE_STAR_SEED_POOL_CAP,
);
seedPool.prewarm(SPACE_STAR_SEED_POOL_CAP);

/**
 * Monotonic counter — each drop gets a unique id even across enemy
 * spawnId reuse. DECISION: deliberately NOT reset in {@link resetSpaceLoot}
 * so a player who restarts a level mid-session keeps minting fresh ids.
 * Earlier behaviour reset to 1 on every level init, which meant
 * `mergeIds` in `persistSpaceLevelCompletion` would collapse the
 * second attempt's `loot-1..loot-N` into the first attempt's. The
 * random suffix on the seedId itself (see {@link maybeSpawnSpaceLoot})
 * also guards against the page-reload case where the module reloads
 * with the counter back at 1.
 */
let nextLootId = 1;

const collectedSeedIdsThisSession: string[] = [];

/**
 * Injectable RNG hook. Default uses `Math.random`; tests can swap this
 * out with a deterministic source via {@link _setSpaceLootRng} so
 * drop-chance branches are unit-testable without flaking on the global
 * PRNG.
 */
let rng: () => number = Math.random;

/**
 * Override the loot RNG (test-only seam). Pass `Math.random` to
 * restore the default behaviour. Module-local rather than exported
 * via a constants file so the production bundle has only one
 * call-site to audit.
 *
 * @param next - The replacement RNG function.
 */
export function _setSpaceLootRng(next: () => number): void {
  rng = next;
}

/**
 * Pool-availability accessor for tests. Returns the number of pooled
 * seeds not currently in use.
 *
 * @returns Slots available in the seed pool.
 */
export function _spaceLootPoolAvailable(): number {
  return seedPool.available;
}

/**
 * Read-only snapshot of seed ids collected during the active session.
 * `persistSpaceLevelCompletion()` reads this at level clear so it can
 * merge them into the save's `levels[levelId].collectedStarSeedIds`.
 *
 * @returns A snapshot copy of the collected-id array.
 */
export function getCollectedSeedIdsThisSession(): string[] {
  return collectedSeedIdsThisSession.slice();
}

/**
 * Release every active pickup back to the pool, clear the scene's
 * `spaceState.loot` array, and drain the per-session collected-id
 * list. Called by `SpaceScene.init()` / `SpaceScene.destroy()`.
 *
 * DECISION: Does NOT reset {@link nextLootId} — see the counter's
 * docblock for the dedupe-collision rationale.
 */
export function resetSpaceLoot(): void {
  const live = spaceState.loot;
  for (let i = live.length - 1; i >= 0; i--) {
    seedPool.release(live[i]);
  }
  live.length = 0;
  collectedSeedIdsThisSession.length = 0;
}

/**
 * Probabilistically drop a Star Seed at the given world coordinates
 * (typically the centre of an enemy that just died). No drop on a
 * failed roll. Pool exhaustion silently drops the surplus.
 *
 * DECISION: The seedId combines a process-lifetime monotonic counter
 * with an 8-char base-36 random suffix (`Math.random` — NOT the
 * injectable `rng` hook, so the test-seam swap does not leak into the
 * id). The monotonic alone is not enough: `persistSpaceLevelCompletion`
 * uses `mergeIds` (Set dedupe) on `collectedStarSeedIds`, so if the
 * counter ever restarts at 1 (page reload, module re-eval) the second
 * run's drops would silently collide with the first's and the player
 * would lose seeds. The random suffix is the page-reload safety net.
 *
 * @param x - Spawn X (world space, top-left of hitbox).
 * @param y - Spawn Y (world space, top-left of hitbox).
 */
export function maybeSpawnSpaceLoot(x: number, y: number): void {
  if (rng() >= SPACE_STAR_SEED_DROP_CHANCE) return;
  const seed = seedPool.acquire();
  if (seed === null) return;
  const suffix = Math.random().toString(36).slice(2, 10);
  seed.spawn(`loot-${nextLootId++}-${suffix}`, x, y);
  spaceState.loot.push(seed);
}

/**
 * Advance every active pickup one tick and reap any that drifted past
 * the camera's left cull margin.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateSpaceLoot(dt: number): void {
  for (let i = spaceState.loot.length - 1; i >= 0; i--) {
    const s = spaceState.loot[i];
    s.update(dt, EMPTY_SPACE_LEVEL);
    if (s.dead) {
      seedPool.release(s);
      spaceState.loot.splice(i, 1);
    }
  }
}

function overlaps(a: AABB, b: AABB): boolean {
  return !(
    a.x + a.width <= b.x ||
    a.x >= b.x + b.width ||
    a.y + a.height <= b.y ||
    a.y >= b.y + b.height
  );
}

/**
 * Pairwise AABB collision between the ship and live pickups. On a hit
 * the pickup's id is appended to {@link collectedSeedIdsThisSession}
 * and the pickup is reaped. Skipped entirely when no ship or game
 * over — mirror of the shard pickup rule.
 */
export function resolveShipLootCollisions(): void {
  const ship = spaceState.ship;
  if (ship === null) return;
  if (spaceState.gameOver) return;
  const shipBox = ship.getHitbox();
  const seeds = spaceState.loot;
  for (let i = 0; i < seeds.length; i++) {
    const s = seeds[i];
    if (s.dead || s.collected) continue;
    if (!overlaps(shipBox, s.getHitbox())) continue;
    s.collected = true;
    s.dead = true;
    s.active = false;
    collectedSeedIdsThisSession.push(s.seedId);
  }
}
